<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  <div class="p-6 sm:p-8">
    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center gap-3 py-12 justify-center">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
        <span class="text-sm text-gray-500 dark:text-gray-400">Cargando correo...</span>
      </div>
    }

    <!-- Error -->
    @if (!loading && error) {
      <div class="rounded-lg bg-error-50 dark:bg-error-500/15 p-4 text-sm text-error-700 dark:text-error-400">
        {{ error }}
      </div>
    }

    @if (!loading && email) {
      <!-- Header -->
      <div class="flex items-start gap-4 mb-6">
        <div class="flex-shrink-0">
          <div class="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400">
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white break-words">
            {{ email.subject || '(Sin asunto)' }}
          </h2>
          <div class="mt-2 flex items-center gap-2 flex-wrap">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              [class]="statusColor(email.status)">
              {{ statusLabel(email.status) }}
            </span>
            <span class="text-xs text-gray-400 dark:text-gray-500">·</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(email.sentAt || email.createdAt) }}</span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav class="-mb-px flex gap-1">
          <button
            (click)="onTabChange('info')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'info'"
            [class.text-brand-600]="activeTab === 'info'"
            [class.dark:text-brand-400]="activeTab === 'info'"
            [class.border-transparent]="activeTab !== 'info'"
            [class.text-gray-500]="activeTab !== 'info'"
            [class.dark:text-gray-400]="activeTab !== 'info'">
            Mensaje
          </button>
          <button
            (click)="onTabChange('eventos')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'eventos'"
            [class.text-brand-600]="activeTab === 'eventos'"
            [class.dark:text-brand-400]="activeTab === 'eventos'"
            [class.border-transparent]="activeTab !== 'eventos'"
            [class.text-gray-500]="activeTab !== 'eventos'"
            [class.dark:text-gray-400]="activeTab !== 'eventos'">
            Eventos ({{ email.events.length }})
          </button>
        </nav>
      </div>

      <!-- ========== TAB: MENSAJE ========== -->
      @if (activeTab === 'info') {
        <div class="space-y-6">
          <!-- De / Para -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400">De</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white break-all">
                {{ email.fromName ? email.fromName + ' <' + email.fromEmail + '>' : email.fromEmail }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400">Para</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white break-all">{{ recipientsTo }}</p>
            </div>
            @if (email.replyTo) {
              <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Responder a</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white break-all">{{ email.replyTo }}</p>
              </div>
            }
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400">Propósito</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ email.purpose }}</p>
            </div>
          </div>

          <!-- Métricas -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div class="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <p class="text-xs text-gray-500 dark:text-gray-400">Aperturas</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ email.openCount }}</p>
            </div>
            <div class="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <p class="text-xs text-gray-500 dark:text-gray-400">Clics</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ email.clickCount }}</p>
            </div>
            <div class="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <p class="text-xs text-gray-500 dark:text-gray-400">Entregado</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(email.deliveredAt) }}</p>
            </div>
            <div class="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3">
              <p class="text-xs text-gray-500 dark:text-gray-400">Primera apertura</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(email.firstOpenedAt) }}</p>
            </div>
          </div>

          @if (email.errorMessage) {
            <div class="rounded-lg bg-error-50 dark:bg-error-500/15 p-3 text-sm text-error-700 dark:text-error-400">
              <span class="font-medium">Error de envío:</span> {{ email.errorMessage }}
            </div>
          }

          <!-- Cuerpo -->
          <div>
            <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Cuerpo del mensaje
            </h3>
            @if (email.bodyHtml) {
              <div class="prose prose-sm dark:prose-invert max-w-none rounded-lg border border-gray-200 dark:border-gray-700 p-4 overflow-x-auto text-gray-900 dark:text-gray-100"
                   [innerHTML]="email.bodyHtml | safeHtml"></div>
            } @else if (email.bodyText) {
              <pre class="whitespace-pre-wrap break-words rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-900 dark:text-gray-100 font-sans">{{ email.bodyText }}</pre>
            } @else {
              <p class="text-sm text-gray-400 dark:text-gray-500">Sin contenido.</p>
            }
          </div>

          <!-- Adjuntos -->
          @if (email.attachments.length > 0) {
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Adjuntos ({{ email.attachments.length }})
              </h3>
              <div class="space-y-2">
                @for (att of email.attachments; track att.id) {
                  <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50">
                    <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                    </svg>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ att.fileName }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ att.mimeType }} · {{ formatSize(att.sizeBytes) }}</p>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- ========== TAB: EVENTOS ========== -->
      @if (activeTab === 'eventos') {
        @if (email.events.length === 0) {
          <div class="text-center py-10 text-gray-400 dark:text-gray-500">
            <svg class="w-10 h-10 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-sm">Aún no hay eventos para este correo.</p>
          </div>
        } @else {
          <div class="relative pl-4">
            <div class="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700"></div>
            <ul class="space-y-5">
              @for (ev of email.events; track ev.id) {
                <li class="relative pl-6">
                  <span class="absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full ring-4 ring-white dark:ring-gray-900 {{ eventDotColor(ev.eventType) }}"></span>
                  <div class="flex items-center justify-between gap-2 flex-wrap">
                    <span class="text-sm font-medium text-gray-900 dark:text-white">{{ eventLabel(ev.eventType) }}</span>
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(ev.occurredAt) }}</span>
                  </div>
                  @if (ev.ipAddress || ev.userAgent) {
                    <p class="mt-1 text-xs text-gray-400 dark:text-gray-500 break-all">
                      @if (ev.ipAddress) { <span>IP: {{ ev.ipAddress }}</span> }
                      @if (ev.ipAddress && ev.userAgent) { <span> · </span> }
                      @if (ev.userAgent) { <span>{{ ev.userAgent }}</span> }
                    </p>
                  }
                </li>
              }
            </ul>
          </div>
        }
      }
    }
  </div>
</app-modal>
